import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Save,
  Eye,
  X,
  BookOpen,
  ListChecks,
  Trophy,
} from "lucide-react";
import { useGlobalState } from "../../GlobalStates";
import Modal from "../../components/common/Modal";
import Toast from "../../components/common/Toast";
import RichTextEditor from "../../components/common/RichTextEditor";
import {
  getCourse,
  updateCourseById,
  addSection,
  updateSection,
  deleteSection,
  addQuestion,
  updateQuestion,
  deleteQuestion,
} from "../../services/courseService";

const QUESTION_TYPES = [
  { value: "single_select", label: "Single Select" },
  { value: "multi_select", label: "Multi Select" },
  { value: "true_false", label: "True / False" },
  { value: "open_ended", label: "Open Ended" },
];

// Slug generator helper
const generateSlug = (title) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

// ---------------------------------------------------------------------------
// Option Editor
// ---------------------------------------------------------------------------

const OptionEditor = ({ options, onChange, correctAnswers, onCorrectChange, questionType }) => {
  const isMulti = questionType === "multi_select";

  const addOption = () => {
    const id = String.fromCharCode(97 + options.length);
    onChange([...options, { id, text: "" }]);
  };

  const removeOption = (idx) => {
    const removed = options[idx];
    const next = options.filter((_, i) => i !== idx);
    onChange(next);
    if (correctAnswers?.includes(removed.id)) {
      onCorrectChange(correctAnswers.filter((a) => a !== removed.id));
    }
  };

  const updateOptionText = (idx, text) => {
    const next = [...options];
    next[idx] = { ...next[idx], text };
    onChange(next);
  };

  const toggleCorrect = (optId) => {
    if (isMulti) {
      const next = (correctAnswers || []).includes(optId)
        ? correctAnswers.filter((a) => a !== optId)
        : [...(correctAnswers || []), optId];
      onCorrectChange(next);
    } else {
      onCorrectChange([optId]);
    }
  };

  return (
    <div className="tw-space-y-2">
      <label className="tw-text-xs tw-font-medium tw-text-gray-500">
        {isMulti
          ? "Options (check all correct)"
          : "Options (select correct)"}
      </label>
      {options.map((opt, idx) => (
          <div key={idx} className="tw-flex tw-items-center tw-gap-2">
            <input
              type={isMulti ? "checkbox" : "radio"}
              name="correct"
              checked={(correctAnswers || []).includes(opt.id)}
              onChange={() => toggleCorrect(opt.id)}
              className="tw-h-4 tw-w-4 tw-flex-none tw-text-primary-main"
            />
            <input
              type="text"
              value={opt.text}
              onChange={(e) => updateOptionText(idx, e.target.value)}
              placeholder={`Option ${opt.id.toUpperCase()}`}
              className="tw-flex-1 tw-rounded-lg tw-border tw-border-gray-200 tw-px-3 tw-py-1.5 tw-text-sm focus:tw-border-primary-main focus:tw-outline-none"
            />
            <button
              onClick={() => removeOption(idx)}
              className="tw-flex-none tw-rounded tw-p-1 tw-text-gray-400 hover:tw-bg-red-50 hover:tw-text-red-500"
            >
              <X className="tw-h-4 tw-w-4" />
            </button>
          </div>
      ))}
      <button
        onClick={addOption}
        className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-primary-main hover:tw-underline"
      >
        <Plus className="tw-h-3 tw-w-3" /> Add option
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Confirm Modal
// ---------------------------------------------------------------------------

const ConfirmModal = ({ open, onClose, onConfirm, title, message, confirmLabel = "Confirm", loading = false }) => (
  <Modal open={open} onClose={onClose} title={title} maxWidth="sm" maxHeight="auto">
    <p className="tw-text-sm tw-text-gray-600">{message}</p>
    <div className="tw-mt-6 tw-flex tw-gap-3">
      <button
        onClick={onClose}
        className="tw-flex-1 tw-rounded-xl tw-border tw-border-gray-200 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-transition-all hover:tw-bg-gray-50"
      >
        Cancel
      </button>
      <button
        onClick={onConfirm}
        disabled={loading}
        className="tw-flex-1 tw-rounded-xl tw-bg-red-500 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-transition-all hover:tw-bg-red-600 disabled:tw-opacity-50"
      >
        {loading ? "Deleting..." : confirmLabel}
      </button>
    </div>
  </Modal>
);

// ---------------------------------------------------------------------------
// Question Card
// ---------------------------------------------------------------------------

const QuestionCard = ({ question, sectionId, onDeleted, onQuestionChange, showToast }) => {
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(!!question._isNew);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const update = (patch) => {
    onQuestionChange(question.id, patch);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        question_text: question.question_text,
        question_type: question.question_type,
        options: question.options || [],
        correct_answers: question.correct_answers || [],
      };
      if (question.question_type === "open_ended") {
        payload.correct_answers = null;
      }
      if (question.question_type === "open_ended") {
        payload.model_answer = question.model_answer || null;
      }
      if (question.question_type === "true_false") {
        payload.options = [
          { id: "true", text: "True" },
          { id: "false", text: "False" },
        ];
      }
      if (question._isNew) {
        const { data } = await addQuestion(sectionId, payload);
        onQuestionChange(question.id, { ...data, _isNew: false });
      } else {
        await updateQuestion(question.id, payload);
      }
      setDirty(false);
      showToast("Question saved", "success");
    } catch (err) {
      console.error("Save question error:", err);
      showToast("Failed to save question", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (!question._isNew) {
        await deleteQuestion(question.id);
      }
      onDeleted(question.id);
      showToast("Question deleted", "success");
    } catch (err) {
      console.error("Delete question error:", err);
      showToast("Failed to delete question", "error");
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleTypeChange = (newType) => {
    const patch = { question_type: newType };
    if (newType === "true_false") {
      patch.options = [
        { id: "true", text: "True" },
        { id: "false", text: "False" },
      ];
    }
    update(patch);
  };

  return (
    <div className={`tw-rounded-lg tw-border ${question._isNew ? "tw-border-amber-300 tw-bg-amber-50" : "tw-border-gray-200 tw-bg-white"} tw-p-4`}>
      {question._isNew && (
        <div className="tw-mb-2 tw-text-xs tw-font-medium tw-text-amber-600">
          Unsaved — fill in details and click Save
        </div>
      )}
      <div className="tw-mb-3 tw-flex tw-items-end tw-justify-between tw-gap-2">
        <div className="tw-flex-1">
          <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-gray-500">Question text</label>
          <RichTextEditor
            value={question.question_text}
            onChange={(val) => update({ question_text: val })}
            placeholder="Question text..."
          />
        </div>
        <button onClick={() => setShowDeleteConfirm(true)} className="tw-mb-1 tw-flex-none tw-rounded tw-p-1.5 tw-text-gray-400 hover:tw-bg-red-50 hover:tw-text-red-500">
          <Trash2 className="tw-h-4 tw-w-4" />
        </button>
      </div>

      <div className="tw-mb-3">
        <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-gray-500">Type</label>
        <select
          value={question.question_type}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="tw-rounded-lg tw-border tw-border-gray-200 tw-px-3 tw-py-1.5 tw-text-sm focus:tw-border-primary-main focus:tw-outline-none"
        >
          {QUESTION_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {["single_select", "multi_select", "true_false"].includes(question.question_type) && (
        <OptionEditor
          options={question.options || []}
          onChange={(opts) => update({ options: opts })}
          correctAnswers={question.correct_answers || []}
          onCorrectChange={(ca) => update({ correct_answers: ca })}
          questionType={question.question_type}
        />
      )}

      {question.question_type === "open_ended" && (
        <div className="tw-mt-3">
          <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-gray-500">Model answer</label>
          <RichTextEditor
            value={question.model_answer || ""}
            onChange={(val) => update({ model_answer: val })}
            placeholder="Write the model answer here..."
          />
        </div>
      )}

      {dirty && (
        <div className="tw-mt-3 tw-flex tw-justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-primary-main tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white hover:tw-bg-primary-main/90 disabled:tw-opacity-50"
          >
            <Save className="tw-h-3 tw-w-3" />
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      )}

      <ConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete question"
        message="Are you sure you want to delete this question? This action cannot be undone."
        confirmLabel="Delete"
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section Accordion
// ---------------------------------------------------------------------------

const SectionAccordion = ({ section, courseId, onUpdated, onDeleted, onSectionChange, showToast }) => {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const updateLocal = (patch) => {
    onSectionChange(section.id, patch);
    setDirty(true);
  };

  const handleSaveSection = async () => {
    setSaving(true);
    try {
      await updateSection(section.id, { title: section.title, intro_text: section.intro_text });
      setDirty(false);
      showToast("Section saved", "success");
    } catch (err) {
      console.error("Save section error:", err);
      showToast("Failed to save section", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async () => {
    try {
      await deleteSection(section.id);
      onDeleted(section.id);
      showToast("Section deleted", "success");
    } catch (err) {
      console.error("Delete section error:", err);
      showToast("Failed to delete section", "error");
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleAddQuestion = () => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const newQuestion = {
      id: tempId,
      _isNew: true,
      section_id: section.id,
      question_text: "",
      question_type: "single_select",
      options: [
        { id: "a", text: "" },
        { id: "b", text: "" },
      ],
      correct_answers: ["a"],
    };
    onSectionChange(section.id, {
      questions: [...(section.questions || []), newQuestion],
    });
    showToast("New question added — fill in details and save", "info");
  };

  const handleQuestionDeleted = (qId) => {
    onSectionChange(section.id, { questions: (section.questions || []).filter((q) => q.id !== qId) });
  };

  const handleQuestionChange = (qId, patch) => {
    onSectionChange(section.id, {
      questions: (section.questions || []).map((q) => (q.id === qId ? { ...q, ...patch } : q)),
    });
  };

  return (
    <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="tw-flex tw-w-full tw-items-center tw-justify-between tw-p-4 tw-text-left hover:tw-bg-gray-100"
      >
        <div className="tw-flex tw-items-center tw-gap-3">
          <GripVertical className="tw-h-4 tw-w-4 tw-text-gray-400" />
          <div>
            <h4 className="tw-font-semibold">{section.title}</h4>
            <p className="tw-text-xs tw-text-gray-500">{(section.questions || []).length} questions</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="tw-h-5 tw-w-5 tw-text-gray-400" /> : <ChevronDown className="tw-h-5 tw-w-5 tw-text-gray-400" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="tw-overflow-hidden"
          >
            <div className="tw-space-y-4 tw-border-t tw-border-gray-200 tw-p-4">
              <div>
                <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-gray-500">Section title</label>
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => updateLocal({ title: e.target.value })}
                  className="tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-primary-main focus:tw-outline-none"
                />
              </div>
              <div>
                <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-gray-500">Introduction text</label>
                <RichTextEditor
                  value={section.intro_text || ""}
                  onChange={(val) => updateLocal({ intro_text: val })}
                  placeholder="Educational content shown before questions..."
                />
              </div>

              {dirty && (
                <div className="tw-flex tw-items-center tw-gap-2">
                  <button
                    onClick={handleSaveSection}
                    disabled={saving}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-primary-main tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white hover:tw-bg-primary-main/90 disabled:tw-opacity-50"
                  >
                    <Save className="tw-h-3 tw-w-3" />
                    {saving ? "Saving..." : "Save section"}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-red-200 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-red-500 hover:tw-bg-red-50"
                  >
                    <Trash2 className="tw-h-3 tw-w-3" />
                    Delete section
                  </button>
                </div>
              )}

              <div className="tw-space-y-3">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <h5 className="tw-text-sm tw-font-semibold tw-text-gray-700">Questions</h5>
                  <button
                    onClick={handleAddQuestion}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-primary-main hover:tw-underline"
                  >
                    <Plus className="tw-h-3 tw-w-3" /> Add question
                  </button>
                </div>
                {(section.questions || []).map((q) => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    sectionId={section.id}
                    onDeleted={handleQuestionDeleted}
                    onQuestionChange={handleQuestionChange}
                    showToast={showToast}
                  />
                ))}
                {(!section.questions || section.questions.length === 0) && (
                  <p className="tw-py-4 tw-text-center tw-text-sm tw-text-gray-400">No questions yet</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteSection}
        title="Delete section"
        message={`Are you sure you want to delete "${section.title}" and all its questions? This action cannot be undone.`}
        confirmLabel="Delete"
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Editor
// ---------------------------------------------------------------------------

const DRAFT_KEY = "course_editor_draft";

const CourseEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [user] = useGlobalState("user");

  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ title: "", slug: "", description: "", conclusion_title: "", conclusion_text: "" });
  const [metaDirty, setMetaDirty] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Build a full draft snapshot (used for preview)
  const buildDraft = () => ({
    ...course,
    sections,
    title: meta.title,
    slug: meta.slug,
    description: meta.description,
    conclusion_title: meta.conclusion_title,
    conclusion_text: meta.conclusion_text,
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/courses");
      return;
    }

    const init = async () => {
      // Restore draft only when returning from preview
      const hasPreviewDraft = sessionStorage.getItem("course_preview_draft");
      const raw = hasPreviewDraft ? sessionStorage.getItem(DRAFT_KEY) : null;
      sessionStorage.removeItem(DRAFT_KEY);

      if (raw) {
        try {
          const draft = JSON.parse(raw);
          if (draft.slug === slug || draft._originalSlug === slug) {
            setCourse(draft);
            setSections(draft.sections || []);
            setMeta({
              title: draft.title || "",
              slug: draft.slug || "",
              description: draft.description || "",
              conclusion_title: draft.conclusion_title || "",
              conclusion_text: draft.conclusion_text || "",
            });
            setLoading(false);
            return;
          }
        } catch (e) {
          // Invalid draft, fall through to API fetch
        }
      }

      try {
        const { data } = await getCourse(slug);
        setCourse(data);
        setSections(data.sections || []);
        setMeta({
          title: data.title || "",
          slug: data.slug || "",
          description: data.description || "",
          conclusion_title: data.conclusion_title || "",
          conclusion_text: data.conclusion_text || "",
        });
      } catch (err) {
        console.error("Failed to load course:", err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [slug, user, navigate]);

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setMeta((m) => ({ ...m, title, slug: generateSlug(title) }));
    setMetaDirty(true);
  };

  const handleSaveMeta = async () => {
    if (!course) return;
    setSavingMeta(true);
    try {
      const payload = { ...meta, status: "published" };
      const { data } = await updateCourseById(course.id, payload);
      setCourse((c) => ({ ...c, ...data }));
      setMetaDirty(false);
      // Clear draft so next editor load fetches fresh from API
      sessionStorage.removeItem(DRAFT_KEY);
      if (data.slug !== slug) {
        navigate(`/courses/${data.slug}/edit`, { replace: true });
      }
      showToast("Course details saved", "success");
    } catch (err) {
      console.error("Save meta error:", err);
      showToast("Failed to save course details", "error");
    } finally {
      setSavingMeta(false);
    }
  };

  const handleSectionChange = (sectionId, patch) => {
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, ...patch } : s))
    );
  };

  const handleAddSection = async () => {
    if (!course) return;
    try {
      const { data } = await addSection(course.id, { title: "New Section", intro_text: "" });
      setSections((prev) => [...prev, { ...data, questions: [] }]);
      showToast("Section added", "success");
    } catch (err) {
      console.error("Add section error:", err);
      showToast("Failed to add section", "error");
    }
  };

  const handleSectionDeleted = (sectionId) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
  };

  if (loading) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-white">
        <div className="tw-w-full tw-max-w-4xl tw-space-y-4 tw-px-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="tw-h-20 tw-animate-pulse tw-rounded-xl tw-bg-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-white">
        <div className="tw-text-center">
          <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900">Course not found</h2>
          <button onClick={() => navigate("/courses")} className="tw-mt-4 tw-text-primary-main hover:tw-underline">
            Back to courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-bg-gray-50 tw-pt-[70px] tw-text-gray-900 md:tw-pt-[86px]">
      {/* Top bar */}
      <div className="tw-sticky tw-top-[70px] tw-z-30 tw-border-b tw-border-gray-200 tw-bg-white md:tw-top-[86px]">
        <div className="tw-container tw-mx-auto tw-flex tw-max-w-5xl tw-items-center tw-justify-between tw-px-4 tw-py-3">
          <button
            onClick={() => {
              sessionStorage.removeItem(DRAFT_KEY);
              sessionStorage.removeItem("course_preview_draft");
              navigate("/courses");
            }}
            className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-text-gray-900"
          >
            <ArrowLeft className="tw-h-4 tw-w-4" />
            Back
          </button>
          <div className="tw-flex tw-items-center tw-gap-2">
            {metaDirty && (
              <button
                onClick={handleSaveMeta}
                disabled={savingMeta}
                className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-primary-main tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary-main/90 disabled:tw-opacity-50"
              >
                <Save className="tw-h-4 tw-w-4" />
                {savingMeta ? "Saving..." : "Save changes"}
              </button>
            )}
            <button
              onClick={() => {
                const draft = buildDraft();
                draft._originalSlug = slug;
                sessionStorage.setItem("course_preview_draft", JSON.stringify(draft));
                sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
                navigate(`/courses/${meta.slug || course.slug}/preview`);
              }}
              className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-gray-200 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-bg-gray-50"
            >
              <Eye className="tw-h-4 tw-w-4" />
              Preview
            </button>
          </div>
        </div>
      </div>

      <div className="tw-container tw-mx-auto tw-max-w-5xl tw-px-4 tw-py-8">
        {/* ============================================================ */}
        {/* SECTION 1: Introduction / Course Details */}
        {/* ============================================================ */}
        <div className="tw-mb-8">
          <div className="tw-mb-4 tw-flex tw-items-center tw-gap-3">
            <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-blue-100">
              <BookOpen className="tw-h-4 tw-w-4 tw-text-blue-600" />
            </div>
            <h2 className="tw-text-lg tw-font-semibold">Introduction</h2>
          </div>
          <div className="tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-p-6">
            <div className="tw-space-y-4">
              <div>
                <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-gray-500">Title</label>
                <input
                  type="text"
                  value={meta.title}
                  onChange={handleTitleChange}
                  className="tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-primary-main focus:tw-outline-none"
                />
              </div>
              <div>
                <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-gray-500">Description</label>
                <RichTextEditor
                  value={meta.description}
                  onChange={(val) => { setMeta((m) => ({ ...m, description: val })); setMetaDirty(true); }}
                  placeholder="Course description..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* SECTION 2: Quiz Sections */}
        {/* ============================================================ */}
        <div className="tw-mb-8">
          <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between">
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-amber-100">
                <ListChecks className="tw-h-4 tw-w-4 tw-text-amber-600" />
              </div>
              <h2 className="tw-text-lg tw-font-semibold">Sections</h2>
              <span className="tw-rounded-full tw-bg-gray-100 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-gray-500">
                {sections.length}
              </span>
            </div>
            <button
              onClick={handleAddSection}
              className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-primary-main tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary-main/90"
            >
              <Plus className="tw-h-4 tw-w-4" />
              Add section
            </button>
          </div>

          <div className="tw-space-y-4">
            {sections.map((section) => (
              <SectionAccordion
                key={section.id}
                section={section}
                courseId={course.id}
                onDeleted={handleSectionDeleted}
                onSectionChange={handleSectionChange}
                showToast={showToast}
              />
            ))}
            {sections.length === 0 && (
              <div className="tw-rounded-2xl tw-bg-white tw-py-12 tw-text-center tw-border tw-border-gray-200">
                <p className="tw-text-gray-400">No sections yet. Add one to get started.</p>
              </div>
            )}
          </div>
        </div>

        {/* ============================================================ */}
        {/* SECTION 3: Conclusion */}
        {/* ============================================================ */}
        <div className="tw-mb-8">
          <div className="tw-mb-4 tw-flex tw-items-center tw-gap-3">
            <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-green-100">
              <Trophy className="tw-h-4 tw-w-4 tw-text-green-600" />
            </div>
            <h2 className="tw-text-lg tw-font-semibold">Conclusion</h2>
          </div>
          <div className="tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-p-6">
            <div className="tw-space-y-4">
              <div>
                <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-gray-500">Conclusion title</label>
                <input
                  type="text"
                  value={meta.conclusion_title}
                  onChange={(e) => { setMeta((m) => ({ ...m, conclusion_title: e.target.value })); setMetaDirty(true); }}
                  className="tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-primary-main focus:tw-outline-none"
                  placeholder="e.g. Congratulations on completing the course!"
                />
              </div>
              <div>
                <label className="tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-gray-500">Conclusion text</label>
                <RichTextEditor
                  value={meta.conclusion_text}
                  onChange={(val) => { setMeta((m) => ({ ...m, conclusion_text: val })); setMetaDirty(true); }}
                  placeholder="Conclusion message shown after course completion..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CourseEditor;
